import React, { useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

function isInvoiceInSelectedFys(inv: any, selectedFys: string[]): boolean {
  if (!selectedFys || selectedFys.length === 0) return true;
  if (inv.financial_year) {
    return selectedFys.includes(inv.financial_year);
  }
  const d = new Date(inv.Date);
  if (isNaN(d.getTime())) return false;
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed: 0 = Jan
  let fy = "";
  if (month >= 3) { // Apr or later
    fy = `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
  } else {
    fy = `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
  }
  return selectedFys.includes(fy);
}

interface Company {
  id: number | string;
  company_name: string;
}

interface Customer {
  id: number;
  company_name: string;
}

interface Vendor {
  id: number;
  company_name: string;
}

interface Invoice {
  id: number;
  Date: string;
  Total_Amount: number;
  SGSTAmount: number;
  CGSTAmount: number;
  IGSTAmount: number;
  InvoiceNo?: string;
  Company_id: number | string;
  Customer_id?: number;
  Vendor_id?: number;
  Particulars?: string;
  HSN_SAC?: string;
  Amounts?: string;
  is_gst_inclusive?: number;
}

const GstReport: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  
  const [salesInvoices, setSalesInvoices] = useState<Invoice[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<Invoice[]>([]);
  
  const [companyId, setCompanyId] = useState<string>(() => {
    const s = localStorage.getItem("selected_company_id");
    return s && s !== "all" ? s : "";
  });
  
  // Custom Date Range Pickers
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [toDate, setToDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  
  const [activeTab, setActiveTab] = useState<'sales' | 'purchases' | 'hsn'>('sales');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const activeWorkspaceId = localStorage.getItem("selected_company_id");
    axios.get(`${API_URL}/auth/companies`).then((res) => {
      const compList = res.data || [];
      if (activeWorkspaceId && activeWorkspaceId !== "all") {
        setCompanies(compList.filter((c: any) => String(c.id) === String(activeWorkspaceId)));
        setCompanyId(String(activeWorkspaceId));
      } else {
        setCompanies(compList);
      }
    });
    axios.get(`${API_URL}/auth/customers`).then((res) => {
      setCustomers(res.data || []);
    });
    axios.get(`${API_URL}/auth/vendors`).then((res) => {
      setVendors(res.data || []);
    });
  }, []);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/auth/invoices`),
      axios.get(`${API_URL}/auth/purchase-invoices`),
      axios.get(`${API_URL}/auth/setting`)
    ]).then(([salesRes, purchRes, settingRes]) => {
      let sales = salesRes.data || [];
      let purchases = purchRes.data || [];
      const settingData = settingRes.data || {};

      let selectedFys: string[] = [];
      try {
        if (settingData.selected_fys) {
          selectedFys = JSON.parse(settingData.selected_fys);
        }
      } catch (e) {}

      if (selectedFys.length > 0) {
        sales = sales.filter((inv: any) => isInvoiceInSelectedFys(inv, selectedFys));
        purchases = purchases.filter((inv: any) => isInvoiceInSelectedFys(inv, selectedFys));
      }

      sales = sales.filter((inv: any) => String(inv.Company_id) === String(companyId));
      purchases = purchases.filter((inv: any) => String(inv.Company_id) === String(companyId));
      setSalesInvoices(sales);
      setPurchaseInvoices(purchases);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [companyId]);

  const filterByRange = (invoices: Invoice[]) => {
    return invoices.filter(inv => {
      if (!inv.Date) return false;
      const dateStr = inv.Date.split('T')[0];
      return dateStr >= fromDate && dateStr <= toDate;
    });
  };

  const currentSales = filterByRange(salesInvoices);
  const currentPurchases = filterByRange(purchaseInvoices);

  // Parse Multi-item rows and assign HSN wise Tax breakdown
  const getInvoiceLineItems = (inv: Invoice) => {
    let particularsArr: string[] = [];
    let hsnArr: string[] = [];
    let amountArr: number[] = [];

    try {
      if (inv.Particulars) {
        particularsArr = JSON.parse(inv.Particulars);
      }
    } catch (e) {
      if (typeof inv.Particulars === 'string' && inv.Particulars) {
        particularsArr = [inv.Particulars];
      }
    }

    try {
      if (inv.HSN_SAC) {
        hsnArr = JSON.parse(inv.HSN_SAC);
      }
    } catch (e) {
      if (typeof inv.HSN_SAC === 'string' && inv.HSN_SAC) {
        hsnArr = [inv.HSN_SAC];
      }
    }

    try {
      if (inv.Amounts) {
        const parsed = JSON.parse(inv.Amounts);
        amountArr = parsed.map((a: any) => Number(a) || 0);
      }
    } catch (e) {
      if (typeof inv.Amounts === 'string' && inv.Amounts) {
        amountArr = [Number(inv.Amounts) || 0];
      }
    }

    const length = Math.max(particularsArr.length, hsnArr.length, amountArr.length, 1);
    const items = [];
    for (let i = 0; i < length; i++) {
      items.push({
        particulars: particularsArr[i] || "",
        hsn_sac: hsnArr[i] || "N/A",
        taxable_amount: amountArr[i] || 0
      });
    }
    return items;
  };

  // HSN Breakdown Calculation
  const calculateHsnBreakdown = (sales: Invoice[], purchases: Invoice[]) => {
    const map: { [hsn: string]: { hsn: string; taxable: number; cgst: number; sgst: number; igst: number; total: number; type: 'sales' | 'purchases' } } = {};

    // Process Sales Invoices
    sales.forEach(inv => {
      const items = getInvoiceLineItems(inv);
      const invoiceTotalTaxable = items.reduce((sum, item) => sum + item.taxable_amount, 0) || 1;
      
      items.forEach(item => {
        const hsn = item.hsn_sac.trim() || "N/A";
        const proportion = item.taxable_amount / invoiceTotalTaxable;
        
        const cgst = (Number(inv.CGSTAmount) || 0) * proportion;
        const sgst = (Number(inv.SGSTAmount) || 0) * proportion;
        const igst = (Number(inv.IGSTAmount) || 0) * proportion;
        const gross = item.taxable_amount + cgst + sgst + igst;

        const key = `sales_${hsn}`;
        if (!map[key]) {
          map[key] = { hsn, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0, type: 'sales' };
        }
        map[key].taxable += item.taxable_amount;
        map[key].cgst += cgst;
        map[key].sgst += sgst;
        map[key].igst += igst;
        map[key].total += gross;
      });
    });

    // Process Purchase Invoices
    purchases.forEach(inv => {
      const items = getInvoiceLineItems(inv);
      const invoiceTotalTaxable = items.reduce((sum, item) => sum + item.taxable_amount, 0) || 1;

      items.forEach(item => {
        const hsn = item.hsn_sac.trim() || "N/A";
        const proportion = item.taxable_amount / invoiceTotalTaxable;

        const cgst = (Number(inv.CGSTAmount) || 0) * proportion;
        const sgst = (Number(inv.SGSTAmount) || 0) * proportion;
        const igst = (Number(inv.IGSTAmount) || 0) * proportion;
        const gross = item.taxable_amount + cgst + sgst + igst;

        const key = `purchase_${hsn}`;
        if (!map[key]) {
          map[key] = { hsn, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0, type: 'purchases' };
        }
        map[key].taxable += item.taxable_amount;
        map[key].cgst += cgst;
        map[key].sgst += sgst;
        map[key].igst += igst;
        map[key].total += gross;
      });
    });

    return Object.values(map);
  };

  const hsnCalculations = calculateHsnBreakdown(currentSales, currentPurchases);

  const calculateGST = (invoices: Invoice[]) => {
    return invoices.reduce((acc, curr) => {
      return acc + (Number(curr.SGSTAmount) || 0) + (Number(curr.CGSTAmount) || 0) + (Number(curr.IGSTAmount) || 0);
    }, 0);
  };
  
  const calculateTotal = (invoices: Invoice[]) => {
    return invoices.reduce((acc, curr) => acc + (Number(curr.Total_Amount) || 0), 0);
  };

  const calculateTaxable = (invoices: Invoice[]) => {
    return invoices.reduce((acc, curr) => {
      const items = getInvoiceLineItems(curr);
      const taxable = items.reduce((sum, i) => sum + i.taxable_amount, 0);
      return acc + taxable;
    }, 0);
  };

  const gstCollected = calculateGST(currentSales);
  const gstPaid = calculateGST(currentPurchases);
  const netGstPayable = gstCollected - gstPaid;

  const totalSalesTaxable = calculateTaxable(currentSales);
  const totalSalesAmount = calculateTotal(currentSales);
  
  const totalPurchaseTaxable = calculateTaxable(currentPurchases);
  const totalPurchaseAmount = calculateTotal(currentPurchases);

  // Quick helper to fetch customer name
  const getCustomerName = (id?: number) => {
    if (!id) return "-";
    const found = customers.find(c => c.id === id);
    return found ? found.company_name : `Customer ID: ${id}`;
  };

  // Quick helper to fetch vendor name
  const getVendorName = (id?: number) => {
    if (!id) return "-";
    const found = vendors.find(v => v.id === id);
    return found ? found.company_name : `Vendor ID: ${id}`;
  };

  // Period Preset Selectors
  const setPeriodPreset = (preset: 'this_month' | 'last_month' | 'this_quarter' | 'this_fy') => {
    const d = new Date();
    const currentYear = d.getFullYear();
    const currentMonth = d.getMonth(); // 0-indexed

    if (preset === 'this_month') {
      setFromDate(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`);
      setToDate(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    } else if (preset === 'last_month') {
      const firstOfLast = new Date(currentYear, currentMonth - 1, 1);
      const lastOfLast = new Date(currentYear, currentMonth, 0);
      setFromDate(`${firstOfLast.getFullYear()}-${String(firstOfLast.getMonth() + 1).padStart(2, '0')}-01`);
      setToDate(`${lastOfLast.getFullYear()}-${String(lastOfLast.getMonth() + 1).padStart(2, '0')}-${String(lastOfLast.getDate()).padStart(2, '0')}`);
    } else if (preset === 'this_quarter') {
      // Quarters starting Apr: Q1 (Apr-Jun), Q2 (Jul-Sep), Q3 (Oct-Dec), Q4 (Jan-Mar)
      let startMonth = 3; // default Q1 Apr
      if (currentMonth >= 3 && currentMonth <= 5) startMonth = 3; // Q1
      else if (currentMonth >= 6 && currentMonth <= 8) startMonth = 6; // Q2
      else if (currentMonth >= 9 && currentMonth <= 11) startMonth = 9; // Q3
      else startMonth = 0; // Q4 (Jan-Mar)

      const startYear = (startMonth === 0 && currentMonth >= 0 && currentMonth <= 2) ? currentYear : currentYear;
      setFromDate(`${startYear}-${String(startMonth + 1).padStart(2, '0')}-01`);
      setToDate(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    } else if (preset === 'this_fy') {
      const fyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
      setFromDate(`${fyStartYear}-04-01`);
      setToDate(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 print:py-0 print:px-0">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b pb-4 print:border-0">
        <h2 className="text-3xl font-extrabold text-blue-900 tracking-tight print:text-xl">
          GST Filing & HSN Summary Report
        </h2>
        <div className="mt-4 md:mt-0 flex gap-3 print:hidden">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-xl text-xs font-bold shadow uppercase transition-all"
          >
            🖨️ Print Report
          </button>
          {companyId && (currentSales.length > 0 || currentPurchases.length > 0) && (
            <button
              onClick={() => {
                const comp = companies.find(c => String(c.id) === String(companyId));
                const gstPayload = {
                  gstin: comp?.company_name || 'GSTIN_NOT_SET',
                  fp: `${fromDate.replace(/-/g, '')}_${toDate.replace(/-/g, '')}`,
                  version: "GST1.0",
                  hash: "hash_value",
                  b2b: currentSales.map(inv => {
                    const sgst = Number(inv.SGSTAmount) || 0;
                    const cgst = Number(inv.CGSTAmount) || 0;
                    const igst = Number(inv.IGSTAmount) || 0;
                    const taxVal = sgst + cgst + igst;
                    const baseVal = Number(inv.Total_Amount) - taxVal;
                    return {
                      ctin: "CUSTOMER_GSTIN",
                      inv: [
                        {
                          inum: inv.InvoiceNo,
                          idt: inv.Date ? new Date(inv.Date).toLocaleDateString('en-GB') : '',
                          val: Number(inv.Total_Amount),
                          pos: "07",
                          rchrg: "N",
                          inv_typ: "R",
                          itms: [
                            {
                              num: 1,
                              itm_det: {
                                txval: baseVal,
                                rt: taxVal > 0 ? Math.round((taxVal / baseVal) * 100) : 18,
                                iamt: igst,
                                camt: cgst,
                                samt: sgst
                              }
                            }
                          ]
                        }
                      ]
                    };
                  }),
                  b2bur: currentPurchases.map(pur => {
                    const sgst = Number(pur.SGSTAmount) || 0;
                    const cgst = Number(pur.CGSTAmount) || 0;
                    const igst = Number(pur.IGSTAmount) || 0;
                    const taxVal = sgst + cgst + igst;
                    const baseVal = Number(pur.Total_Amount) - taxVal;
                    return {
                      inv: [
                        {
                          inum: pur.InvoiceNo,
                          idt: pur.Date ? new Date(pur.Date).toLocaleDateString('en-GB') : '',
                          val: Number(pur.Total_Amount),
                          itms: [
                            {
                              num: 1,
                              itm_det: {
                                txval: baseVal,
                                rt: taxVal > 0 ? Math.round((taxVal / baseVal) * 100) : 18,
                                iamt: igst,
                                camt: cgst,
                                samt: sgst
                              }
                            }
                          ]
                        }
                      ]
                    };
                  })
                };
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(gstPayload, null, 2));
                const downloadAnchor = document.createElement('a');
                downloadAnchor.setAttribute("href", dataStr);
                downloadAnchor.setAttribute("download", `GST_FILING_REPORT_${fromDate}_TO_${toDate}.json`);
                document.body.appendChild(downloadAnchor);
                downloadAnchor.click();
                downloadAnchor.remove();
              }}
              className="px-4 py-2 bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white rounded-xl text-xs font-bold shadow uppercase transition-all"
            >
              📥 Export GST Offline JSON
            </button>
          )}
        </div>
      </div>

      {/* Control Filters Panel */}
      <div className="bg-white shadow-xl rounded-3xl border border-slate-100 p-6 mb-8 flex flex-wrap gap-6 justify-between items-end print:hidden">
        <div className="flex flex-wrap gap-6 items-end">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Company</label>
            <select
              className="form-select border-2 border-slate-200 rounded-xl p-3 min-w-[240px] text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={companyId}
              onChange={e => setCompanyId(e.target.value)}
            >
              <option value="">-- Choose Company --</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">From Date</label>
            <input
              type="date"
              className="form-input border-2 border-slate-200 rounded-xl p-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">To Date</label>
            <input
              type="date"
              className="form-input border-2 border-slate-200 rounded-xl p-3 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setPeriodPreset('this_month')} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors">This Month</button>
          <button onClick={() => setPeriodPreset('last_month')} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors">Last Month</button>
          <button onClick={() => setPeriodPreset('this_quarter')} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors">This Qtr</button>
          <button onClick={() => setPeriodPreset('this_fy')} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors">This FY</button>
        </div>
      </div>

      {!companyId ? (
        <div className="text-center text-slate-400 py-16 bg-white rounded-3xl shadow-md border border-slate-100 font-medium">
          Please select a company to retrieve and view the GST Filing Report.
        </div>
      ) : loading ? (
        <div className="text-center text-blue-600 py-16 bg-white rounded-3xl shadow-md border border-slate-100 font-semibold animate-pulse">
          Retrieving sales ledger, purchase ledger and calculating tax tables...
        </div>
      ) : (
        <div className="space-y-8">
          
          {/* Key Tax Summary KPI Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-3xl p-6 text-center shadow-sm">
              <h3 className="text-blue-800 font-bold text-sm uppercase tracking-wider mb-2">Total Sales Taxable</h3>
              <p className="text-3xl font-black text-blue-900">₹ {totalSalesTaxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}</p>
              <p className="text-xs text-slate-500 mt-2">GST Collected: <b>₹ {gstCollected.toLocaleString("en-IN")}</b> ({currentSales.length} Invoices)</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 border border-yellow-200 rounded-3xl p-6 text-center shadow-sm">
              <h3 className="text-yellow-800 font-bold text-sm uppercase tracking-wider mb-2">Total Purchase Taxable</h3>
              <p className="text-3xl font-black text-yellow-900">₹ {totalPurchaseTaxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}</p>
              <p className="text-xs text-slate-500 mt-2">ITC Eligible: <b>₹ {gstPaid.toLocaleString("en-IN")}</b> ({currentPurchases.length} Invoices)</p>
            </div>
            <div className={`border rounded-3xl p-6 text-center shadow-sm bg-gradient-to-br ${netGstPayable > 0 ? "from-red-50 to-red-100/50 border-red-200" : "from-green-50 to-green-100/50 border-green-200"}`}>
              <h3 className={`font-bold text-sm uppercase tracking-wider mb-2 ${netGstPayable > 0 ? "text-red-800" : "text-green-800"}`}>Net GST Payable</h3>
              <p className={`text-3xl font-black ${netGstPayable > 0 ? "text-red-900" : "text-green-900"}`}>
                ₹ {Math.abs(netGstPayable).toLocaleString("en-IN", {minimumFractionDigits: 2})}
              </p>
              <p className={`text-xs mt-2 font-semibold ${netGstPayable > 0 ? "text-red-600" : "text-green-600"}`}>
                {netGstPayable > 0 ? "⚠️ Net liability to be paid to Government" : "✅ Available Input Tax Credit carryforward"}
              </p>
            </div>
          </div>

          {/* Navigation Tabs (Hidden in Print mode, print mode renders everything in list format) */}
          <div className="flex border-b border-slate-200 gap-4 print:hidden">
            <button
              onClick={() => setActiveTab('sales')}
              className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all ${activeTab === 'sales' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              📊 Sales Registry ({currentSales.length})
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all ${activeTab === 'purchases' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              📥 Purchases Registry ({currentPurchases.length})
            </button>
            <button
              onClick={() => setActiveTab('hsn')}
              className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all ${activeTab === 'hsn' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              🏷️ HSN Summary Table ({hsnCalculations.length})
            </button>
          </div>

          {/* Sales Tab Content */}
          {(activeTab === 'sales' || window.matchMedia('print').matches) && (
            <div className="bg-white rounded-3xl shadow-md border border-slate-100 overflow-hidden print:shadow-none print:border-0">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-slate-900 font-extrabold text-base">Sales Invoices B2B Registry Ledger</h3>
                <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-3 py-1 rounded-full print:hidden">
                  Period: {new Date(fromDate).toLocaleDateString("en-IN")} to {new Date(toDate).toLocaleDateString("en-IN")}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 border-b">
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">#</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Invoice No.</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Billed To (Party Name)</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Date</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">Taxable Val (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">SGST (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">CGST (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">IGST (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">Gross Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSales.length > 0 ? currentSales.map((inv, idx) => {
                      const items = getInvoiceLineItems(inv);
                      const baseVal = items.reduce((sum, item) => sum + item.taxable_amount, 0);
                      return (
                        <tr key={inv.id || idx} className="hover:bg-slate-50/50 border-b border-slate-100 transition-colors">
                          <td className="py-3 px-4 font-medium text-slate-500">{idx + 1}</td>
                          <td className="py-3 px-4 font-bold text-blue-700">
                            {inv.InvoiceNo || '-'}
                          </td>
                          <td className="py-3 px-4 font-semibold text-slate-800">{getCustomerName(inv.Customer_id)}</td>
                          <td className="py-3 px-4 text-slate-600">{inv.Date ? new Date(inv.Date).toLocaleDateString("en-IN") : '-'}</td>
                          <td className="py-3 px-4 text-right text-slate-800 font-medium">{baseVal.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{Number(inv.SGSTAmount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{Number(inv.CGSTAmount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{Number(inv.IGSTAmount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right font-bold text-slate-900">{Number(inv.Total_Amount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={9} className="py-8 text-center text-slate-400 font-medium">No sales invoices found matching selected dates.</td></tr>
                    )}
                    {currentSales.length > 0 && (
                      <tr className="bg-blue-50/80 font-bold text-blue-900 border-t">
                        <td colSpan={4} className="py-3 px-4">TOTAL</td>
                        <td className="py-3 px-4 text-right">{totalSalesTaxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right">{currentSales.reduce((s, i) => s + (Number(i.SGSTAmount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right">{currentSales.reduce((s, i) => s + (Number(i.CGSTAmount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right">{currentSales.reduce((s, i) => s + (Number(i.IGSTAmount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right text-blue-950 font-black">{totalSalesAmount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Purchases Tab Content */}
          {(activeTab === 'purchases' || window.matchMedia('print').matches) && (
            <div className="bg-white rounded-3xl shadow-md border border-slate-100 overflow-hidden print:shadow-none print:border-0 print:mt-8">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-slate-900 font-extrabold text-base">Purchase Invoices ITC Registry Ledger</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 border-b">
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">#</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Invoice No.</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Purchased From (Vendor)</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Date</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">Taxable Val (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">SGST (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">CGST (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">IGST (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">Gross Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPurchases.length > 0 ? currentPurchases.map((inv, idx) => {
                      const items = getInvoiceLineItems(inv);
                      const baseVal = items.reduce((sum, item) => sum + item.taxable_amount, 0);
                      return (
                        <tr key={inv.id || idx} className="hover:bg-slate-50/50 border-b border-slate-100 transition-colors">
                          <td className="py-3 px-4 font-medium text-slate-500">{idx + 1}</td>
                          <td className="py-3 px-4 font-bold text-yellow-700">
                            {inv.InvoiceNo || '-'}
                          </td>
                          <td className="py-3 px-4 font-semibold text-slate-800">{getVendorName(inv.Vendor_id)}</td>
                          <td className="py-3 px-4 text-slate-600">{inv.Date ? new Date(inv.Date).toLocaleDateString("en-IN") : '-'}</td>
                          <td className="py-3 px-4 text-right text-slate-800 font-medium">{baseVal.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{Number(inv.SGSTAmount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{Number(inv.CGSTAmount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{Number(inv.IGSTAmount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="py-3 px-4 text-right font-bold text-slate-900">{Number(inv.Total_Amount || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={9} className="py-8 text-center text-slate-400 font-medium">No purchase invoices found matching selected dates.</td></tr>
                    )}
                    {currentPurchases.length > 0 && (
                      <tr className="bg-yellow-50/80 font-bold text-yellow-900 border-t">
                        <td colSpan={4} className="py-3 px-4">TOTAL</td>
                        <td className="py-3 px-4 text-right">{totalPurchaseTaxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right">{currentPurchases.reduce((s, i) => s + (Number(i.SGSTAmount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right">{currentPurchases.reduce((s, i) => s + (Number(i.CGSTAmount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right">{currentPurchases.reduce((s, i) => s + (Number(i.IGSTAmount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right text-yellow-950 font-black">{totalPurchaseAmount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* HSN Summary Tab Content */}
          {(activeTab === 'hsn' || window.matchMedia('print').matches) && (
            <div className="bg-white rounded-3xl shadow-md border border-slate-100 overflow-hidden print:shadow-none print:border-0 print:mt-8">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-slate-900 font-extrabold text-base">HSN/SAC Code wise Tax Calculations</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 border-b">
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">HSN/SAC Code</th>
                      <th className="py-3 px-4 text-left font-bold uppercase tracking-wider">Registry Type</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">Total Taxable Amount (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">SGST Amount (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">CGST Amount (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">IGST Amount (₹)</th>
                      <th className="py-3 px-4 text-right font-bold uppercase tracking-wider">Total Gross Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hsnCalculations.length > 0 ? hsnCalculations.map((calc, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50 border-b border-slate-100 transition-colors">
                        <td className="py-3 px-4 font-bold text-slate-900 bg-slate-50/30">{calc.hsn}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded font-bold uppercase text-[9px] ${calc.type === 'sales' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                            {calc.type === 'sales' ? 'Sales Output' : 'Purchase Input'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-slate-800 font-medium">{calc.taxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{calc.sgst.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{calc.cgst.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{calc.igst.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        <td className="py-3 px-4 text-right font-bold text-slate-900">{calc.total.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={7} className="py-8 text-center text-slate-400 font-medium">No HSN/SAC transaction lines processed in this period.</td></tr>
                    )}
                    {hsnCalculations.length > 0 && (
                      <tr className="bg-blue-50/80 font-bold text-blue-900 border-t">
                        <td colSpan={2} className="py-3 px-4">TOTALS AGGREGATED</td>
                        <td className="py-3 px-4 text-right">
                          {hsnCalculations.reduce((sum, c) => sum + c.taxable, 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {hsnCalculations.reduce((sum, c) => sum + c.sgst, 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {hsnCalculations.reduce((sum, c) => sum + c.cgst, 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {hsnCalculations.reduce((sum, c) => sum + c.igst, 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}
                        </td>
                        <td className="py-3 px-4 text-right text-blue-950 font-black">
                          {hsnCalculations.reduce((sum, c) => sum + c.total, 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GstReport;

